/**
 * Activities — `activities.html` ported in full: the KPI strip, the tabbed
 * planner, a "needs attention" panel and the team feed, not just the planner
 * table the previous version of this screen had.
 *
 * Two panels deliberately do **not** match the prototype's fixtures:
 *
 * - The prototype's "SLA breaches" card implies a real escalation-rule engine
 *   (§14 automation) that does not exist anywhere in this build. Rather than
 *   fabricate rule names and breach timers, this panel is real data instead —
 *   the caller's own most-overdue open activities, sorted worst first.
 * - The prototype's "Team activity feed" shows cross-module events (deals
 *   won/lost, stage moves, quotation approvals, leads imported). Building
 *   that needs pulling from several modules at once; `docs/API.md` §8 itself
 *   says `GET /activities` *is* the activity feed, so this panel is exactly
 *   that call, recent-first — narrower than the prototype's story, but real.
 *   `FeedPage.jsx` (the dedicated Workspace → Feed screen) reads the same way.
 *   The card's `All/Mentions/Mine` toggle and comment composer are otherwise
 *   ported in full — see `feedFormat.jsx` for what's real (`All`/`Mine`, via
 *   `owner_id`) and what's disabled chrome with an explanation (`Mentions`,
 *   comments — neither has a backing resource yet).
 *
 * List, Calendar and Follow-ups are one screen, not three: a `?view=` toggle
 * swaps the tabbed table for `CalendarView` (`CalendarPage.jsx`) or
 * `FollowUpsView` (`ActivitiesPages.jsx`), the same move already made for
 * Opportunities/Pipeline — see `setView` below and the comments on
 * `AppRoutes.jsx`'s `/calendar` and `/follow-ups` redirects. The KPI strip
 * stays put across all three views since it's relevant regardless of how
 * you're browsing; the right-hand panels (Needs attention, Team feed) are
 * List-only — Calendar and Follow-ups already fill the width on their own.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { DataTable } from '../../components/common/DataTable.jsx';
import {
  PageHeader, Card, StatTile, Badge, Avatar, SearchInput, ChipSelect, FilterSelect,
  Drawer, Modal, Field, Tabs, MockBadge, Spinner, EmptyState, ErrorState,
} from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import { dueLabel, relativeTime } from '../../utils/formatters.js';
import { queryKeys } from '../../app/queryClient.js';
import * as usersApi from '../../api/users.api.js';
import * as leadsApi from '../../api/leads.api.js';
import * as accountsApi from '../../api/accounts.api.js';
import * as opportunitiesApi from '../../api/opportunities.api.js';
import { useOpenComplete, ACTIVITY_COMPLETED_EVENT } from './CompleteActivityModal.jsx';
import { useActivities, useActivitiesFeed, useCreateActivity, useCompleteActivity, useDeleteActivity } from './useActivities.js';
import { useFollowUps } from './useFollowUps.js';
import {
  KIND_META, KIND_LABEL, KIND_OPTIONS, PRIORITY_OPTIONS,
  ActivityDetail, DeleteActivityModal, EditActivityModal, relatedRecordLinks, StagePill,
} from './ActivityTimeline.jsx';
import { feedVerb, FeedFilterSeg, useFeedScroll } from './feedFormat.jsx';
import CalendarView from './CalendarPage.jsx';
import { FollowUpsView } from './ActivitiesPages.jsx';

const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'completed', label: 'Completed' },
  { key: 'all', label: 'All activities' },
];

function tabParams(key) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 86_400_000 - 1);
  const in7Days = new Date(startOfToday.getTime() + 7 * 86_400_000);
  switch (key) {
    case 'today': return { due_after: startOfToday.toISOString(), due_before: endOfToday.toISOString() };
    case 'overdue': return { overdue: 'true' };
    /* From now, not from tomorrow: a call booked for 11:30 this afternoon is still upcoming. */
    case 'upcoming': return { due_after: now.toISOString(), due_before: in7Days.toISOString() };
    case 'completed': return { state: 'completed' };
    default: return {};
  }
}

export default function ActivitiesPage() {
  const { can, hasScope, canOpen } = useAuth();
  const canLog = can('activities', 'create');
  const canComplete = can('activities', 'edit');
  const canDelete = can('activities', 'delete');
  const canFilterByOwner = hasScope('activities', 'team');

  /* `calendar` and `followups` stay their own screen permissions (RBAC and
     the seed both still define them) even though neither is its own route
     any more — mirrors how Opportunities gates its Board toggle on the
     `pipeline` screen rather than assuming `opportunities:view` covers it. */
  const canCalendar = canOpen('calendar');
  const canFollowups = canOpen('followups');
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const view = (requestedView === 'calendar' && canCalendar) ? 'calendar'
    : (requestedView === 'followups' && canFollowups) ? 'followups'
      : 'list';
  const setView = (next) => setSearchParams(
    (prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'list') params.delete('view'); else params.set('view', next);
      return params;
    },
    { replace: true },
  );

  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [priority, setPriority] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [tab, setTab] = useState('today');
  const [drawerActivity, setDrawerActivity] = useState(null);
  useEffect(() => {
    const done = () => setDrawerActivity(null);
    window.addEventListener(ACTIVITY_COMPLETED_EVENT, done);
    return () => window.removeEventListener(ACTIVITY_COMPLETED_EVENT, done);
  }, []);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const debounced = useDebounce(search);
  const pages = useCursorPages(20);

  const params = useMemo(() => ({
    ...pages.params,
    ...tabParams(tab),
    q: debounced || undefined,
    kind: kind || undefined,
    priority: priority || undefined,
    owner_id: ownerId || undefined,
    sort: tab === 'completed' ? '-completed_at' : '-created_at',
  }), [pages.params, tab, debounced, kind, priority, ownerId]);

  const { data, isLoading, error, refetch } = useActivities(params);

  const usersQuery = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    enabled: canFilterByOwner,
    staleTime: 300_000,
  });
  const ownerOptions = [
    { value: 'me', label: 'Me' },
    ...(usersQuery.data?.data ?? []).map((u) => ({ value: u.id, label: u.full_name ?? u.email })),
  ];

  const completeMutation = useCompleteActivity();
  const openComplete = useOpenComplete();
  const deleteMutation = useDeleteActivity();

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };
  const onTab = (key) => { setTab(key); pages.reset(); };

  const rows = data?.data ?? [];

  const columns = [
    {
      key: 'subject',
      header: 'Activity',
      render: (a) => {
        const [icon, color] = KIND_META[a.kind] ?? ['bi-circle', 'var(--ink-3)'];
        const related = relatedRecordLinks(a.related);
        return (
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-circle"
              style={{ width: 30, height: 30, background: `color-mix(in srgb, ${color} 16%, #fff)`, flex: 'none' }}
            >
              <i className={`bi ${icon}`} style={{ color }} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <div className="fw-650 t-sm text-truncate">{a.subject}</div>
              <div className="t-xs text-muted text-truncate">
                {KIND_LABEL[a.kind] ?? a.kind}
                {related.length > 0 && <> · {related.map((r) => r.text).join(', ')}</>}
              </div>
              <StagePill activity={a} className="mt-1" />
            </div>
          </div>
        );
      },
    },
    {
      key: 'owner',
      header: 'Owner',
      width: 170,
      render: (a) => (a.owner ? (
        <div className="d-flex align-items-center gap-2">
          <Avatar name={a.owner.full_name} size={22} /><span className="t-sm text-truncate">{a.owner.full_name}</span>
        </div>
      ) : <span className="text-muted">—</span>),
    },
    {
      key: 'when',
      header: 'When',
      width: 150,
      render: (a) => {
        const value = a.scheduled_start ?? a.due_at;
        return value ? <Badge tone={a.is_overdue ? 'rose' : 'neutral'}>{dueLabel(value)}</Badge> : <span className="text-muted">—</span>;
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      width: 100,
      render: (a) => (a.priority && a.priority !== 'Normal'
        ? <Badge tone={a.priority === 'Urgent' ? 'rose' : 'amber'}>{a.priority}</Badge>
        : <span className="text-muted">—</span>),
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (a) => (
        <Badge tone={a.state === 'completed' ? 'emerald' : a.state === 'cancelled' ? 'neutral' : a.is_overdue ? 'rose' : 'sky'}>
          {a.state === 'completed' ? 'Completed' : a.state === 'cancelled' ? 'Cancelled' : a.is_overdue ? 'Overdue' : 'Open'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 110,
      align: 'end',
      render: (a) => (canComplete && a.state !== 'completed' && a.state !== 'cancelled' ? (
        <button
          type="button"
          className="btn-x btn-ghost btn-sm-x"
          onClick={(e) => { e.stopPropagation(); openComplete(a); }}
          disabled={completeMutation.isPending}
        >
          <i className="bi bi-check2 me-1" aria-hidden="true" />Complete
        </button>
      ) : null),
    },
  ];

  return (
    <>
      <PageHeader
        title="Activities"
        subtitle="Calls, meetings, emails, tasks and visits."
        badge={isMock(data) ? <MockBadge section="activities" /> : null}
      >
        {(canCalendar || canFollowups) && (
          <div className="seg">
            <button type="button" className={view === 'list' ? 'active' : ''} title="List" onClick={() => setView('list')}>
              <i className="bi bi-list-ul" aria-hidden="true" />
            </button>
            {canCalendar && (
              <button type="button" className={view === 'calendar' ? 'active' : ''} title="Calendar" onClick={() => setView('calendar')}>
                <i className="bi bi-calendar3" aria-hidden="true" />
              </button>
            )}
            {canFollowups && (
              <button type="button" className={view === 'followups' ? 'active' : ''} title="Follow-ups" onClick={() => setView('followups')}>
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        {canLog && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={() => setComposerOpen(true)}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />Log activity
          </button>
        )}
      </PageHeader>

      <KpiRow />

      {view === 'calendar' ? <CalendarView /> : view === 'followups' ? <FollowUpsView /> : (
        <div className="row g-3">
          <div className="col-xl-7">
            <Card bodyClass="p-0" className="mb-3">
              <div className="px-3 pt-3">
                <Tabs tabs={TABS} value={tab} onChange={onTab} />
              </div>
              <div className="filter-bar">
                <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search subject or notes…" className="flex-grow-1" />
                <ChipSelect label="Kind" allLabel="All kinds" value={kind} onChange={onFilter(setKind)} options={KIND_OPTIONS} />
                <ChipSelect label="Priority" allLabel="All priorities" value={priority} onChange={onFilter(setPriority)} options={PRIORITY_OPTIONS} />
                {canFilterByOwner && (
                  <FilterSelect label="Owner" value={ownerId} onChange={onFilter(setOwnerId)} options={ownerOptions} allLabel="Everyone" />
                )}
              </div>

              <DataTable
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                error={error}
                onRetry={refetch}
                onRowClick={(a) => setDrawerActivity(a)}
                page={data?.page}
                onNext={pages.goNext}
                onPrevious={pages.goPrevious}
                canGoPrevious={pages.canGoPrevious}
                pageSize={pages.limit}
                onPageSizeChange={pages.changeLimit}
                emptyTitle="Nothing here"
                emptyMessage="No activities match this tab and these filters."
              />
            </Card>
          </div>

          <div className="col-xl-5">
            <NeedsAttentionCard onOpen={setDrawerActivity} />
            <TeamFeedCard />
          </div>
        </div>
      )}

      <Drawer
        open={Boolean(drawerActivity)}
        onClose={() => setDrawerActivity(null)}
        title={drawerActivity?.subject}
        subtitle={drawerActivity ? (KIND_LABEL[drawerActivity.kind] ?? drawerActivity.kind) : undefined}
      >
        {drawerActivity && (
          <ActivityDetail
            activity={drawerActivity}
            canComplete={canComplete}
            canDelete={canDelete}
            canEdit={canComplete}
            busy={completeMutation.isPending || deleteMutation.isPending}
            onComplete={() => openComplete(drawerActivity)}
            onDelete={() => setDeleteTarget(drawerActivity)}
            onEdit={() => setEditTarget(drawerActivity)}
          />
        )}
      </Drawer>

      <DeleteActivityModal
        activity={deleteTarget}
        busy={deleteMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async (reason) => {
          await deleteMutation.mutateAsync({ id: deleteTarget.id, reason, rowVersion: deleteTarget.row_version });
          setDeleteTarget(null);
          setDrawerActivity(null);
        }}
      />

      <LogActivityModal open={composerOpen} onClose={() => setComposerOpen(false)} />

      <EditActivityModal
        activity={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); setDrawerActivity(null); }}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI strip                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The four tiles `activities.html` opens with. Three come straight from
 * `GET /follow-ups`'s own `counts` — that endpoint's whole reason for
 * existing is to answer exactly these questions, so nothing here recomputes
 * them. "Completed today" has no matching query param on `GET /activities`
 * (only `due_at`/`scheduled_start` windows, not `completed_at`), so it is the
 * caller's most-recently-completed activities, capped at 100 and counted
 * client-side for the ones that fall on today's calendar date — real counts
 * from real rows, just bounded rather than an exact aggregate.
 */
function KpiRow() {
  const { data: followUps } = useFollowUps({});
  const { data: completedData } = useActivities({ state: 'completed', sort: '-completed_at', limit: 100 });

  const completedToday = useMemo(() => {
    const today = new Date().toDateString();
    return (completedData?.data ?? []).filter((a) => a.completed_at && new Date(a.completed_at).toDateString() === today).length;
  }, [completedData]);

  const counts = followUps?.counts;

  return (
    <div className="row g-3 mb-3">
      <div className="col-6 col-lg-3">
        <StatTile label="Today's follow-ups" value={counts?.today ?? '—'} icon="bi-calendar-check-fill" tone="brand" />
      </div>
      <div className="col-6 col-lg-3">
        <StatTile label="Overdue" value={counts?.overdue ?? '—'} icon="bi-exclamation-triangle-fill" tone="rose" />
      </div>
      <div className="col-6 col-lg-3">
        <StatTile label="Upcoming (7 days)" value={counts?.this_week ?? '—'} icon="bi-clock-fill" tone="sky" />
      </div>
      <div className="col-6 col-lg-3">
        <StatTile label="Completed today" value={completedToday} icon="bi-check2-circle" tone="emerald" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Needs attention (replaces the prototype's SLA-breach card)                  */
/* -------------------------------------------------------------------------- */

/**
 * The caller's most-overdue open activities. Stands in for `activities.html`'s
 * "SLA breaches" card — that one implies real escalation rules (§14
 * automation), which nothing in this build has; this is real overdue work
 * instead of invented rule names and breach timers.
 */
function NeedsAttentionCard({ onOpen }) {
  const { data, isLoading } = useActivities({ overdue: 'true', sort: 'due_at', limit: 5 });
  const rows = data?.data ?? [];

  return (
    <Card
      className="mb-3"
      style={{ borderColor: '#fcd9de' }}
      headStyle={{ background: 'linear-gradient(120deg,#fff1f2,#fff)' }}
      title="Needs attention"
      subtitle="Your most overdue open activities"
    >
      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState icon="bi-check2-circle" title="Nothing overdue" message="Every open activity is on schedule." />
      ) : (
        <div className="d-flex flex-column gap-2">
          {rows.map((a) => (
            <div key={a.id} className="d-flex align-items-center gap-2 p-2 px-3" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)' }}>
              <span className="dot-ind" style={{ background: 'var(--rose)' }} />
              <div className="flex-grow-1 min-w-0">
                <div className="t-sm fw-600 text-truncate">{a.subject}</div>
                <div className="t-2xs ink-4">
                  {relatedRecordLinks(a.related).map((r) => r.text).join(', ') || 'No related record'} · {dueLabel(a.due_at ?? a.scheduled_start)}
                </div>
              </div>
              <button type="button" className="btn btn-brand btn-sm-x no-wrap" onClick={() => onOpen(a)}>Act</button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Team activity feed                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Chat-style, same as `FeedPage.jsx`'s full-page feed: oldest at top, newest
 * at bottom, opens scrolled to the bottom, and scrolling to the top of this
 * card's own scroll box pages in 10 more. 10 and 10, not 25 and 20 — this is
 * a sidebar card, not the full viewport `/feed` gets.
 */
function TeamFeedCard() {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');

  const params = useMemo(() => ({
    owner_id: filter === 'mine' ? user?.id : undefined,
  }), [filter, user?.id]);

  const {
    data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useActivitiesFeed(params, true, { first: 10, more: 10 });

  const rows = useMemo(
    () => [...(data?.pages ?? [])].flatMap((page) => page.data ?? []).reverse(),
    [data],
  );

  const { scrollRef, onScroll, resetScroll } = useFeedScroll(rows, { hasNextPage, isFetchingNextPage, fetchNextPage });

  return (
    <Card
      bodyClass="p-0"
      title="Team activity feed"
      actions={(
        <>
          <FeedFilterSeg value={filter} onChange={(v) => { setFilter(v); resetScroll(); }} />
          <Link to="/feed" className="t-xs fw-650 no-wrap">View all <i className="bi bi-arrow-right" aria-hidden="true" /></Link>
        </>
      )}
    >
      {isLoading ? (
        <div className="p-3"><Spinner /></div>
      ) : error ? (
        <div className="p-3"><ErrorState error={error} onRetry={refetch} /></div>
      ) : rows.length === 0 ? (
        <div className="p-3">
          <EmptyState icon="bi-clock-history" title="Nothing yet" message="Activity will show up here as soon as something is logged." />
        </div>
      ) : (
        <div ref={scrollRef} onScroll={onScroll} className="p-3" style={{ maxHeight: 340, overflowY: 'auto' }}>
          {isFetchingNextPage && (
            <div className="d-flex justify-content-center py-2 t-xs text-muted"><ButtonSpinner />Loading older…</div>
          )}
          {!hasNextPage && !isFetchingNextPage && (
            <div className="text-center t-2xs ink-4 pb-2">Start of the feed</div>
          )}
          <div className="tl">
            {rows.map((item) => {
              const [icon, color] = KIND_META[item.kind] ?? ['bi-circle', 'var(--ink-3)'];
              const { verb } = feedVerb(item);
              const related = relatedRecordLinks(item.related);
              const when = item.completed_at ?? item.created_at;
              return (
                <div key={item.id} className="tl-item">
                  <div className="tl-dot" style={{ background: color }}><i className={`bi ${icon}`} aria-hidden="true" /></div>
                  <div className="tl-title">
                    <span className="fw-600">{item.owner?.full_name ?? 'Someone'}</span> {verb} {(KIND_LABEL[item.kind] ?? item.kind).toLowerCase()}: {item.subject}
                  </div>
                  <div className="tl-meta">
                    {related.length ? related.map((r) => r.text).join(', ') : 'No related record'} · {relativeTime(when)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}


/* -------------------------------------------------------------------------- */
/* Log activity                                                               */
/* -------------------------------------------------------------------------- */

/** Which entities a standalone activity can be logged against, and how to
 * search/display each — lead, account and opportunity cover the common
 * cases; quotation/campaign are valid per the DB CHECK but have no simple
 * list-and-search screen of their own to reuse here. */
const RELATABLE_TYPES = [
  { key: 'lead_id', label: 'Lead', search: (q) => leadsApi.listLeads({ q, limit: 8 }), display: (r) => r.full_name ?? r.company_name ?? r.lead_no },
  { key: 'account_id', label: 'Account', search: (q) => accountsApi.listAccounts({ q, limit: 8 }), display: (r) => r.name ?? r.account_no },
  { key: 'opportunity_id', label: 'Opportunity', search: (q) => opportunitiesApi.listOpportunities({ q, limit: 8 }), display: (r) => r.name ?? r.opportunity_no },
];

/**
 * A live search over leads/accounts/opportunities for the composer's
 * "related to" field. Nothing like this existed anywhere in the codebase —
 * every other screen that needs a related record already has it fixed by
 * page context (a lead's own detail page, say).
 */
function RelatedRecordPicker({ value, onChange }) {
  const [typeKey, setTypeKey] = useState(value?.type ?? RELATABLE_TYPES[0].key);
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const type = RELATABLE_TYPES.find((t) => t.key === typeKey);

  const { data, isFetching } = useQuery({
    queryKey: ['activities-related-search', typeKey, debounced],
    queryFn: () => type.search(debounced),
    enabled: debounced.trim().length >= 2,
  });
  const results = data?.data ?? [];

  if (value) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Badge tone="brand">{RELATABLE_TYPES.find((t) => t.key === value.type)?.label}</Badge>
        <span className="t-sm fw-650 text-truncate">{value.label}</span>
        <button type="button" className="btn-x btn-ghost btn-sm-x ms-auto" onClick={() => onChange(null)}>Change</button>
      </div>
    );
  }

  return (
    <div>
      <div className="seg mb-2">
        {RELATABLE_TYPES.map((t) => (
          <button
            key={t.key} type="button" className={typeKey === t.key ? 'active' : ''}
            onClick={() => { setTypeKey(t.key); setQuery(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <input
        className="form-control" placeholder={`Search ${type.label.toLowerCase()}s…`}
        value={query} onChange={(e) => setQuery(e.target.value)}
      />
      {debounced.trim().length >= 2 && (
        <div className="border rounded-3 mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
          {isFetching ? (
            <div className="p-2 t-sm text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-2 t-sm text-muted">No matches.</div>
          ) : results.map((r) => (
            <button
              key={r.id} type="button" className="d-block w-100 text-start btn btn-ghost btn-sm-x"
              onClick={() => onChange({ type: typeKey, id: r.id, label: type.display(r) })}
            >
              {type.display(r)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LogActivityModal({ open, onClose }) {
  const create = useCreateActivity();
  const [kind, setKind] = useState('task');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [scheduledAt, setScheduledAt] = useState('');
  const [related, setRelated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setKind('task'); setSubject(''); setBody(''); setPriority('Normal'); setScheduledAt(''); setRelated(null);
  }, [open]);

  const canSave = Boolean(subject.trim() && related);

  const save = async () => {
    if (!canSave) return;
    const payload = { kind, subject: subject.trim(), [related.type]: related.id };
    if (body.trim()) payload.body = body.trim();
    if (priority !== 'Normal') payload.priority = priority;
    if (scheduledAt) payload.scheduled_start = new Date(scheduledAt).toISOString();
    /* Logging a call with no future time attached is recording something
       that already happened, not scheduling something to do — same
       reasoning as ActivityTab's composer. */
    if (kind === 'call' && !scheduledAt) {
      payload.state = 'completed';
      payload.completed_at = new Date().toISOString();
    }
    await create.mutateAsync(payload);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log activity"
      footer={(
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" onClick={save} disabled={!canSave || create.isPending}>
            {create.isPending ? <><ButtonSpinner />Saving…</> : 'Save'}
          </button>
        </>
      )}
    >
      <Field label="Kind">
        <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          {KIND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Subject" required>
        <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus />
      </Field>
      <Field label="Related to" required help="Every activity needs at least one record it's about.">
        <RelatedRecordPicker value={related} onChange={setRelated} />
      </Field>
      <Field label="Notes">
        <textarea className="form-control" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      <div className="row g-2">
        <div className="col-6">
          <Field label="Priority">
            <select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-6">
          <Field label="Scheduled for" help="Optional — leave blank for a plain log entry.">
            <input type="datetime-local" className="form-control" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
